import { useCallback, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";

import type { IStructureTreePoint } from "../types";
import { mapPointApiResponse, sortPointsByUserSortNum } from "../utils/structure-tree-utils";

export interface ITrainPointsCacheState {
  pointsByTrainId: Readonly<Record<number, IStructureTreePoint[]>>;
  loadingTrainIds: ReadonlySet<number>;
  loadTrainPoints: (trainId: number) => Promise<IStructureTreePoint[]>;
}

export const useTrainPointsCache = (): ITrainPointsCacheState => {
  const cacheRef = useRef<Record<number, IStructureTreePoint[]>>({});
  const requestsRef = useRef<Record<number, Promise<IStructureTreePoint[]>>>({});
  const [pointsByTrainId, setPointsByTrainId] = useState<Record<number, IStructureTreePoint[]>>({});
  const [loadingTrainIds, setLoadingTrainIds] = useState<ReadonlySet<number>>(() => new Set());

  const { fn: fetchTrainPoints } = useApiCall(
    async (api, { trainId }: { trainId: number }) => {
      const response = (await api.fetchJson({
        url: "/point/train/autocomplete",
        method: "GET",
        data: { idTrain: trainId, measureType: 0 },
      })) as Record<string, unknown>[] | null | undefined;

      return sortPointsByUserSortNum(
        (Array.isArray(response) ? response : []).map(row => ({
          ...mapPointApiResponse(row),
          trainId,
        })),
      );
    },
    { success: false },
  );

  const loadTrainPoints = useCallback(
    async (trainId: number): Promise<IStructureTreePoint[]> => {
      const cachedPoints = cacheRef.current[trainId];

      if (cachedPoints) {
        return cachedPoints;
      }

      const pendingRequest = requestsRef.current[trainId];
      if (pendingRequest !== undefined) {
        return pendingRequest;
      }

      setLoadingTrainIds(previous => new Set(previous).add(trainId));

      const request = fetchTrainPoints(undefined, { trainId })
        .then(points => {
          if (!points) {
            return [];
          }

          cacheRef.current[trainId] = points;
          setPointsByTrainId(previous => ({ ...previous, [trainId]: points }));
          return points;
        })
        .finally(() => {
          delete requestsRef.current[trainId];
          setLoadingTrainIds(previous => {
            const next = new Set(previous);
            next.delete(trainId);
            return next;
          });
        });

      requestsRef.current[trainId] = request;
      return request;
    },
    [fetchTrainPoints],
  );

  return {
    pointsByTrainId,
    loadingTrainIds,
    loadTrainPoints,
  };
};
